import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { SwitchTransition } from 'react-transition-group';
import { Queue } from '@rossum/api-client/queues';
import { SchemaField } from '@rossum/api-client/schemaFields';
import { AnnotationListColumns, SchemaColumn } from '@rossum/api-client/shared';
import { DetailDrawer } from '@rossum/ui';
import {
  IconArrowLeft,
  IconCirclePlus,
  IconLayoutColumns,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  Grow,
  IconButton,
  IconButtonProps,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';
import { isUserAdmin } from '../../redux/modules/user/selectors';
import { AddColumns } from './AddColumns';
import { ColumnList } from './ColumnList';
import { PanelColumn } from './types';

type Props = {
  columns: Array<PanelColumn>;
  selectionActive: boolean;
  activeQueue: Queue | null;
  handleAddColumn: (selectedSchemaField: SchemaField) => void;
  handleRemoveColumn: (column: SchemaColumn) => void;
  updateColumns: (columns: AnnotationListColumns) => void;
  size?: IconButtonProps['size'];
};

type Views = 'columnList' | 'addColumns';
const defaultView: Views = 'columnList';

export const ColumnsPanel = ({
  columns,
  selectionActive,
  activeQueue,
  handleAddColumn,
  handleRemoveColumn,
  updateColumns,
  size,
}: Props) => {
  const intl = useIntl();
  const [view, setView] = useState<Views>(defaultView);
  const [isOpen, setIsOpen] = useState(false);
  const isUserAdminLike = useSelector(isUserAdmin);

  const handleClose = () => {
    setIsOpen(false);
    setView(defaultView);
  };

  return isUserAdminLike ? (
    <Stack>
      <IconButton
        color="secondary"
        size={size}
        disabled={selectionActive}
        onClick={() => setIsOpen(true)}
      >
        <SvgIcon fontSize={size}>
          <IconLayoutColumns />
        </SvgIcon>
      </IconButton>

      <DetailDrawer
        open={isOpen}
        onClose={handleClose}
        titleIcon={
          <IconButton
            onClick={() => setView('columnList')}
            sx={{
              visibility: view === 'addColumns' ? 'visible' : 'hidden',
            }}
          >
            <SvgIcon fontSize="large">
              <IconArrowLeft />
            </SvgIcon>
          </IconButton>
        }
        title={intl.formatMessage({
          id:
            view === 'columnList'
              ? 'containers.allDocuments.columnsPanel.title.listView'
              : 'containers.allDocuments.columnsPanel.title.addView',
        })}
        size="small"
      >
        <Stack px={3} height={1}>
          <SwitchTransition>
            <Grow key={view} appear unmountOnExit timeout={200}>
              <Stack overflow="hidden" flex={1}>
                {view === 'columnList' ? (
                  <Stack pb={4} overflow="hidden">
                    <Button
                      startIcon={
                        <SvgIcon fontSize="large">
                          <IconCirclePlus />
                        </SvgIcon>
                      }
                      color="secondary"
                      sx={{ width: 'fit-content', mt: 3, mb: 2, mx: 'auto' }}
                      onClick={() => setView('addColumns')}
                    >
                      {intl.formatMessage({
                        id: 'containers.allDocuments.columnsPanel.button.add',
                      })}
                    </Button>

                    <ColumnList
                      columns={columns}
                      handleRemoveColumn={handleRemoveColumn}
                      updateColumns={updateColumns}
                    />
                  </Stack>
                ) : (
                  <AddColumns
                    activeQueue={activeQueue}
                    handleAddColumn={handleAddColumn}
                    schemaColumns={columns.flatMap(c =>
                      c.columnType === 'schema' ? [c] : []
                    )}
                    handleRemoveColumn={handleRemoveColumn}
                  />
                )}
              </Stack>
            </Grow>
          </SwitchTransition>
        </Stack>
      </DetailDrawer>
    </Stack>
  ) : null;
};
